import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Mark, mergeAttributes } from '@tiptap/core';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useOutreachAssets,
  useUploadOutreachAsset,
} from '../../hooks/useOutreach';
import type { SendMode } from '../../types/outreach';
import styles from '../pages/OutreachPage.module.css';


/**
 * WYSIWYG editor for campaign step body templates. HTML in / HTML out --
 * the underlying field stays `body_html_template`, so the send pipeline
 * is unchanged. Adds:
 *   * Formatting toolbar (bold/italic/underline/headings/lists/alignment)
 *   * Font size + color (TextStyle marks; output as inline styles --
 *     widely supported by Outlook / Gmail / Apple Mail)
 *   * Merge-field dropdown (filtered by send_mode)
 *   * Link insertion with merge-field-aware URL field
 *   * Image insertion (URL paste OR pick from uploaded assets OR upload new)
 *   * "View HTML" toggle for power users / debugging
 *
 * The editor canvas and the saved-preview pane share the same CSS via
 * EMAIL_BODY_CSS so spacing/typography is identical regardless of where
 * the HTML renders. Export the rendered helper so StepRow's preview
 * stays in lockstep without us having to thread the CSS by hand.
 */


// ---------------------------------------------------------------------------
// Shared email-body CSS. Used by the TipTap canvas, the saved-state preview
// in StepRow, and (best-effort) email-client rendering. Margins are em-based
// to match what email clients (Outlook/Gmail) render by default for <p>
// elements -- typically `1em 0`. Browser-default behavior, applied
// explicitly here so editor and preview render identically regardless of
// what TipTap's ProseMirror layer does to its own canvas.
// ---------------------------------------------------------------------------

export const EMAIL_BODY_CSS = `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #222;
`.trim();

// `font-size: 14px; line-height: 1.5` mirrors what Outlook/Gmail typically
// render bare body HTML at, so margins computed in em (~14px) line up
// across all three contexts without us needing to thread sizes by hand.
const EMAIL_CHILD_RULES = `
  .email-body p { margin: 0 0 1em; }
  .email-body p:last-child { margin-bottom: 0; }
  .email-body h1 { font-size: 1.6em; font-weight: 600; margin: 1em 0 0.5em; line-height: 1.2; }
  .email-body h2 { font-size: 1.3em; font-weight: 600; margin: 1em 0 0.5em; line-height: 1.25; }
  .email-body h3 { font-size: 1.1em; font-weight: 600; margin: 1em 0 0.5em; line-height: 1.3; }
  .email-body a { color: #1a4f8a; text-decoration: underline; }
  .email-body blockquote { border-left: 3px solid #ccc; padding-left: 12px; color: #555; margin: 1em 0; }
  .email-body ul, .email-body ol { padding-left: 22px; margin: 0 0 1em; }
  .email-body li { margin-bottom: 0.25em; }
  .email-body img { max-width: 100%; height: auto; border-radius: 4px; }
  .email-body hr { border: 0; border-top: 1px solid #E5E5E0; margin: 1em 0; }
`;


/**
 * Render saved HTML using the same CSS the editor uses, so previews
 * and editor look identical. Pass the saved HTML as `html`.
 */
export function EmailBodyPreview({ html }: { html: string }) {
  return (
    <>
      <style>{EMAIL_CHILD_RULES}</style>
      <div
        className="email-body"
        style={{
          ...parseCssBlock(EMAIL_BODY_CSS),
          background: '#FFF',
          border: '1px solid #E5E5E0',
          borderRadius: 4,
          padding: 12,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}


function parseCssBlock(block: string): React.CSSProperties {
  const out: Record<string, string> = {};
  for (const line of block.split(';')) {
    const [k, v] = line.split(':').map((s) => s.trim());
    if (!k || !v) continue;
    // Convert kebab-case to camelCase for React style
    const key = k.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    out[key] = v;
  }
  return out as React.CSSProperties;
}


// ---------------------------------------------------------------------------
// Custom FontSize mark. TipTap's TextStyle gives us a generic span with a
// style attribute; we extend it so the toolbar can set/unset font-size
// independently of color. Outputs `<span style="font-size:Xpx">` which
// is universally email-safe.
// ---------------------------------------------------------------------------

const FontSizeMark = Mark.create({
  name: 'fontSize',
  addOptions() {
    return { HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (el: HTMLElement) => el.style.fontSize || null,
        renderHTML: (attrs: { size: string | null }) =>
          attrs.size ? { style: `font-size: ${attrs.size}` } : {},
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span',
        getAttrs: (el: HTMLElement) =>
          el.style.fontSize ? { size: el.style.fontSize } : false,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ commands }: { commands: { setMark: (name: string, attrs: object) => boolean } }) =>
          commands.setMark('fontSize', { size }),
      unsetFontSize:
        () =>
        ({ commands }: { commands: { unsetMark: (name: string) => boolean } }) =>
          commands.unsetMark('fontSize'),
    } as Record<string, unknown>;
  },
});


const FONT_SIZE_PRESETS: { label: string; px: string | null }[] = [
  { label: 'Default', px: null },
  { label: 'Small', px: '11px' },
  { label: 'Medium', px: '14px' },
  { label: 'Large', px: '17px' },
  { label: 'X-Large', px: '20px' },
  { label: 'Huge', px: '24px' },
];

const COLOR_PRESETS: string[] = [
  '#222222', '#555555', '#888888',
  '#B04030', '#7B5400', '#2E6E2E',
  '#1a4f8a', '#6A2A6A', '#E65100',
];


const PER_PERSON_FIELDS: { token: string; label: string; isUrl?: boolean }[] = [
  { token: 'FirstName', label: 'First name' },
  { token: 'LastName', label: 'Last name' },
  { token: 'Email', label: 'Email' },
  { token: 'Company', label: 'Company' },
  { token: 'Title', label: 'Title' },
  { token: 'Sender.FirstName', label: 'Sender first name' },
  { token: 'Sender.LastName', label: 'Sender last name' },
  { token: 'Sender.Email', label: 'Sender email' },
  { token: 'Campaign.Name', label: 'Campaign name' },
  { token: 'Today', label: "Today's date" },
  { token: 'ResponseUrl', label: 'RSVP form URL', isUrl: true },
  { token: 'ResponseUrlYes', label: 'RSVP "Yes" URL', isUrl: true },
  { token: 'ResponseUrlNo', label: 'RSVP "No" URL (autosubmit)', isUrl: true },
  { token: 'UnsubscribeUrl', label: 'Unsubscribe URL', isUrl: true },
];

const PER_ORG_FIELDS: { token: string; label: string; isUrl?: boolean }[] = [
  { token: 'Company', label: 'Company' },
  { token: 'FirstNamesAlpha', label: 'First names (alpha-joined)' },
  { token: 'LastNamesAlpha', label: 'Last names (alpha-joined)' },
  { token: 'Sender.FirstName', label: 'Sender first name' },
  { token: 'Sender.LastName', label: 'Sender last name' },
  { token: 'Sender.Email', label: 'Sender email' },
  { token: 'Campaign.Name', label: 'Campaign name' },
  { token: 'Today', label: "Today's date" },
  { token: 'ResponseUrl', label: 'RSVP form URL', isUrl: true },
  { token: 'ResponseUrlYes', label: 'RSVP "Yes" URL', isUrl: true },
  { token: 'ResponseUrlNo', label: 'RSVP "No" URL (autosubmit)', isUrl: true },
  { token: 'UnsubscribeUrl', label: 'Unsubscribe URL', isUrl: true },
];


/**
 * A list-scoped custom merge field (Feature 2) the picker should
 * surface alongside the built-ins. Caller pulls these from the
 * campaign's affinity_list_custom_fields via
 * `useListCustomFields(campaign.affinity_list_id)`.
 */
export interface CustomMergeFieldOption {
  token: string;
  label: string;
}


export function RichEmailEditor({
  value,
  onChange,
  sendMode,
  disabled,
  customFields,
}: {
  value: string;
  onChange: (html: string) => void;
  sendMode: SendMode;
  disabled?: boolean;
  customFields?: CustomMergeFieldOption[];
}) {
  const [showSource, setShowSource] = useState(false);
  const [sourceText, setSourceText] = useState(value);
  const [showImageDialog, setShowImageDialog] = useState(false);
  // Link dialog state: when null the dialog is closed. When non-null it
  // either holds {} (insert mode, blank inputs) or pre-filled values
  // (edit mode -- cursor was on an existing link when the button fired).
  const [linkDialog, setLinkDialog] = useState<{
    mode: 'insert' | 'edit';
    initialHref: string;
    initialText: string;
  } | null>(null);
  const [showMergeMenu, setShowMergeMenu] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        HTMLAttributes: { style: 'max-width:100%;height:auto;' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontSizeMark,
    ],
    content: value || '<p></p>',
    editable: !disabled,
    // Suppress browser navigation on <a> clicks inside the canvas. Tiptap's
    // openOnClick=false prevents its own dispatcher, but the underlying
    // anchor still fires the browser's default navigate. Clicks on links
    // should place the cursor (so the user can edit the surrounding text)
    // and never leave the page. Returning false from handleClick lets
    // ProseMirror continue and place the caret.
    editorProps: {
      handleClick(view, _pos, event) {
        let el = event.target as HTMLElement | null;
        while (el && el !== view.dom) {
          if (el.tagName === 'A') {
            event.preventDefault();
            return false;
          }
          el = el.parentElement;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // Re-sync editor when the parent value changes externally (e.g., when
  // the StepEditor opens a different step).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '<p></p>', { emitUpdate: false });
    }
  }, [editor, value]);

  function commitSourceAndExit() {
    onChange(sourceText);
    if (editor) {
      editor.commands.setContent(sourceText || '<p></p>', { emitUpdate: false });
    }
    setShowSource(false);
  }

  function enterSource() {
    if (editor) setSourceText(editor.getHTML());
    setShowSource(true);
  }

  const fields = useMemo(() => {
    const builtins = sendMode === 'per_organization'
      ? PER_ORG_FIELDS
      : PER_PERSON_FIELDS;
    // List-scoped custom merge fields (Feature 2). Same picker for
    // both send modes -- the value resolution differs by mode (per-
    // person uses recipient's value; per-org uses first non-empty
    // across the org) but the token is the same in templates either
    // way.
    const customs: { token: string; label: string }[] =
      (customFields || []).map((cf) => ({
        token: cf.token, label: cf.label,
      }));
    // De-dupe by token (defensive — if an admin somehow names a
    // custom field after a built-in, the reserved-name check should
    // have rejected it server-side, but skip just in case).
    const seen = new Set(builtins.map((f) => f.token));
    const merged = [...builtins];
    for (const c of customs) {
      if (seen.has(c.token)) continue;
      merged.push(c);
      seen.add(c.token);
    }
    return merged;
  }, [sendMode, customFields]);

  function openLinkDialog() {
    if (!editor) return;
    if (editor.isActive('link')) {
      // Edit existing link: extend selection across the whole link
      // mark, then read href + visible text. extendMarkRange grows
      // the selection to cover the entire contiguous run that has
      // the link mark, regardless of where the cursor currently sits.
      editor.chain().focus().extendMarkRange('link').run();
      const href = (editor.getAttributes('link') as { href?: string }).href ?? '';
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, ' ', ' ');
      setLinkDialog({ mode: 'edit', initialHref: href, initialText: text });
    } else {
      // Insert: prefill display-text from any non-empty selection so
      // the user doesn't lose context after opening the dialog.
      const { from, to, empty } = editor.state.selection;
      const text = empty
        ? ''
        : editor.state.doc.textBetween(from, to, ' ', ' ');
      setLinkDialog({ mode: 'insert', initialHref: 'https://', initialText: text });
    }
  }

  function applyLinkFromDialog(href: string, displayText: string) {
    if (!editor) return;
    if (linkDialog?.mode === 'edit') {
      // The link mark is currently selected (we extended in
      // openLinkDialog). Replace the selection with the new display
      // text + new link. We compute the new range up front so we can
      // re-select the inserted text and apply setLink in one chain.
      editor.chain().focus().extendMarkRange('link').run();
      const insertText = displayText || href;
      const { from } = editor.state.selection;
      editor
        .chain().focus()
        .extendMarkRange('link')
        .insertContent(insertText)
        .setTextSelection({ from, to: from + insertText.length })
        .setLink({ href, target: '_blank' })
        .run();
    } else {
      const { from, to, empty } = editor.state.selection;
      if (empty) {
        const text = displayText || href;
        editor.chain().focus()
          .insertContent(`<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`)
          .run();
      } else if (displayText && displayText !== editor.state.doc.textBetween(from, to, ' ', ' ')) {
        // User edited the display text -- replace the selection with
        // the new text and apply the link.
        editor.chain().focus()
          .insertContent(displayText)
          .setTextSelection({ from, to: from + displayText.length })
          .setLink({ href, target: '_blank' })
          .run();
      } else {
        editor.chain().focus().setLink({ href, target: '_blank' }).run();
      }
    }
    setLinkDialog(null);
  }

  function removeLinkFromDialog() {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkDialog(null);
  }

  function insertMergeField(token: string, asLink: boolean) {
    if (!editor) return;
    const tokenStr = `{{${token}}}`;
    if (asLink) {
      const { from, to, empty } = editor.state.selection;
      if (empty) {
        editor
          .chain().focus()
          .insertContent(`<a href="${tokenStr}">RSVP</a>`)
          .run();
      } else {
        editor
          .chain().focus()
          .setTextSelection({ from, to })
          .setLink({ href: tokenStr })
          .run();
      }
    } else {
      editor.chain().focus().insertContent(tokenStr).run();
    }
    setShowMergeMenu(false);
  }

  function applyFontSize(px: string | null) {
    if (!editor) return;
    if (px === null) {
      // Unset -- use the underlying mark API directly
      editor.chain().focus().unsetMark('fontSize').run();
    } else {
      editor.chain().focus().setMark('fontSize', { size: px }).run();
    }
    setShowSizeMenu(false);
  }

  function applyColor(hex: string | null) {
    if (!editor) return;
    if (hex === null) {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(hex).run();
    }
    setShowColorMenu(false);
  }

  if (!editor) return null;

  return (
    <div
      style={{
        border: '1px solid #D5D5D0',
        borderRadius: 4,
        background: '#FFF',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4,
          padding: 6, borderBottom: '1px solid #E5E5E0',
          background: '#FAF9F4',
        }}
      >
        <ToolbarBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="B" title="Bold (Ctrl+B)" bold
        />
        <ToolbarBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="I" title="Italic (Ctrl+I)" italic
        />
        <ToolbarBtn
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="U" title="Underline (Ctrl+U)" underline
        />
        <ToolbarSep />

        {/* Font size dropdown */}
        <div style={{ position: 'relative' }}>
          <ToolbarBtn
            onClick={() => setShowSizeMenu((s) => !s)}
            active={showSizeMenu}
            label="Size ▾" title="Font size"
          />
          {showSizeMenu && (
            <DropdownPanel onClose={() => setShowSizeMenu(false)}>
              {FONT_SIZE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyFontSize(p.px)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: 'none', border: 0,
                    padding: '6px 12px', cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: p.px || '13px',
                  }}
                >
                  {p.label}
                  {p.px && (
                    <span
                      style={{
                        color: '#888', fontSize: 11, marginLeft: 8,
                        fontFamily: "'DM Mono', monospace",
                      }}
                    >
                      {p.px}
                    </span>
                  )}
                </button>
              ))}
            </DropdownPanel>
          )}
        </div>

        {/* Color picker */}
        <div style={{ position: 'relative' }}>
          <ToolbarBtn
            onClick={() => setShowColorMenu((s) => !s)}
            active={showColorMenu}
            label="A▾" title="Text color"
          />
          {showColorMenu && (
            <DropdownPanel onClose={() => setShowColorMenu(false)}>
              <div style={{ padding: 8 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 24px)',
                    gap: 4,
                    marginBottom: 8,
                  }}
                >
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => applyColor(c)}
                      title={c}
                      style={{
                        width: 24, height: 24, padding: 0,
                        background: c, border: '1px solid #ccc',
                        borderRadius: 3, cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: '#666',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Custom:{' '}
                  <input
                    type="color"
                    onChange={(e) => applyColor(e.target.value)}
                    style={{
                      width: 24, height: 24, padding: 0, border: 0,
                      cursor: 'pointer',
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => applyColor(null)}
                  style={{
                    display: 'block', width: '100%', marginTop: 6,
                    padding: '4px 8px', fontSize: 11,
                    background: '#FFF', border: '1px solid #D5D5D0',
                    borderRadius: 3, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Reset to default
                </button>
              </div>
            </DropdownPanel>
          )}
        </div>

        <ToolbarSep />
        <ToolbarBtn
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          label="H1" title="Heading 1"
        />
        <ToolbarBtn
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="H2" title="Heading 2"
        />
        <ToolbarBtn
          active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          label="¶" title="Paragraph"
        />
        <ToolbarSep />
        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="• List" title="Bullet list"
        />
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="1. List" title="Numbered list"
        />
        <ToolbarSep />
        <ToolbarBtn
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="❝" title="Blockquote"
        />
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          label="◧" title="Align left"
        />
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          label="◫" title="Align center"
        />
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          label="◨" title="Align right"
        />
        <ToolbarSep />
        <ToolbarBtn
          active={editor.isActive('link')}
          onClick={openLinkDialog}
          label="🔗"
          title={editor.isActive('link') ? 'Edit link' : 'Insert link'}
        />
        <ToolbarBtn
          onClick={() => setShowImageDialog(true)}
          label="🖼" title="Insert image"
        />
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="─" title="Horizontal rule"
        />
        <ToolbarSep />
        <div style={{ position: 'relative' }}>
          <ToolbarBtn
            onClick={() => setShowMergeMenu((s) => !s)}
            label="{{ }}" title="Insert merge field"
            active={showMergeMenu}
          />
          {showMergeMenu && (
            <MergeFieldMenu
              fields={fields}
              onPick={(token, asLink) => insertMergeField(token, asLink)}
              onClose={() => setShowMergeMenu(false)}
            />
          )}
        </div>
        <span style={{ flex: 1 }} />
        <ToolbarBtn
          active={showSource}
          onClick={() => (showSource ? commitSourceAndExit() : enterSource())}
          label={showSource ? '◀ WYSIWYG' : '< / > HTML'}
          title="Toggle raw HTML view"
        />
      </div>

      {/* Editor canvas */}
      {showSource ? (
        <textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          style={{
            width: '100%', minHeight: 240, padding: 12,
            fontFamily: "'DM Mono', monospace", fontSize: 12,
            border: 0, outline: 'none', resize: 'vertical',
          }}
          disabled={disabled}
        />
      ) : (
        <EditorContent
          editor={editor}
          className="email-body rich-email-editor"
        />
      )}

      {linkDialog && (
        <LinkDialog
          fields={fields}
          mode={linkDialog.mode}
          initialHref={linkDialog.initialHref}
          initialText={linkDialog.initialText}
          onClose={() => setLinkDialog(null)}
          onApply={applyLinkFromDialog}
          onRemove={linkDialog.mode === 'edit' ? removeLinkFromDialog : undefined}
        />
      )}
      {showImageDialog && (
        <ImageDialog
          onClose={() => setShowImageDialog(false)}
          onApply={(url, linkUrl) => {
            if (linkUrl) {
              // Insert image, then select that single image node and
              // apply the Link mark. Tiptap's Link extension validates
              // hrefs (no javascript:/data: schemes) so this is XSS-safe
              // even when linkUrl comes from operator input.
              const from = editor.state.selection.from;
              editor.chain().focus().setImage({ src: url }).run();
              editor.chain()
                .setTextSelection({ from, to: from + 1 })
                .setLink({ href: linkUrl, target: '_blank' })
                .run();
            } else {
              editor.chain().focus().setImage({ src: url }).run();
            }
            setShowImageDialog(false);
          }}
        />
      )}

      {/* Editor canvas typography. We re-emit EMAIL_CHILD_RULES with two
          parallel selectors -- one that targets the wrapper directly and
          one that targets `.ProseMirror p` etc. explicitly. ProseMirror
          injects its own UA-style overrides that win against bare
          descendant selectors otherwise; pinning specificity here forces
          editor canvas to match the saved-state preview pixel-for-pixel. */}
      <style>{`
        .rich-email-editor.email-body {
          ${EMAIL_BODY_CSS}
          min-height: 240px;
          outline: none;
        }
        .rich-email-editor.email-body .ProseMirror {
          ${EMAIL_BODY_CSS}
          padding: 12px 14px;
          min-height: 240px;
          outline: none;
        }
        ${EMAIL_CHILD_RULES.replace(
          /\.email-body /g,
          '.rich-email-editor.email-body .ProseMirror ',
        )}
      `}</style>
    </div>
  );
}


function ToolbarBtn({
  active, onClick, label, title, bold, italic, underline,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  title: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        background: active ? '#222' : '#FFF',
        color: active ? '#FFF' : '#333',
        border: '1px solid #D5D5D0',
        borderRadius: 3,
        padding: '3px 8px',
        fontSize: 12,
        fontFamily: "'DM Sans', sans-serif",
        cursor: 'pointer',
        fontWeight: bold ? 700 : 500,
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: underline ? 'underline' : 'none',
        minWidth: 28,
      }}
    >
      {label}
    </button>
  );
}


function ToolbarSep() {
  return (
    <span style={{ borderLeft: '1px solid #D5D5D0', margin: '2px 4px' }} />
  );
}


function DropdownPanel({
  children, onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 100 }}
      />
      <div
        style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          background: '#FFF', border: '1px solid #D5D5D0',
          borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          minWidth: 160, zIndex: 101,
          maxHeight: 360, overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </>
  );
}


function MergeFieldMenu({
  fields, onPick, onClose,
}: {
  fields: { token: string; label: string; isUrl?: boolean }[];
  onPick: (token: string, asLink: boolean) => void;
  onClose: () => void;
}) {
  return (
    <DropdownPanel onClose={onClose}>
      <div
        style={{
          padding: '6px 10px', fontSize: 11, color: '#888',
          textTransform: 'uppercase', letterSpacing: '0.04em',
          borderBottom: '1px solid #E5E5E0',
          fontFamily: "'DM Mono', monospace",
        }}
      >
        Insert merge field
      </div>
      {fields.map((f) => (
        <div
          key={f.token}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px',
            borderBottom: '1px solid #F5F4EF',
            fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={() => onPick(f.token, false)}
            style={{
              flex: 1, textAlign: 'left',
              background: 'none', border: 0, padding: '2px 0',
              cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
            }}
            title={`Insert {{${f.token}}} at cursor`}
          >
            <span style={{ color: '#333' }}>{f.label}</span>
            <span
              style={{
                color: '#888', fontSize: 11, marginLeft: 6,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              {`{{${f.token}}}`}
            </span>
          </button>
          {f.isUrl && (
            <button
              type="button"
              onClick={() => onPick(f.token, true)}
              style={{
                fontSize: 10, padding: '2px 6px',
                border: '1px solid #D5D5D0', borderRadius: 3,
                background: '#FFF', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
              title="Wrap selected text in <a href={{token}}>"
            >
              Insert as link
            </button>
          )}
        </div>
      ))}
    </DropdownPanel>
  );
}


function LinkDialog({
  fields, mode, initialHref, initialText, onApply, onRemove, onClose,
}: {
  fields: { token: string; label: string; isUrl?: boolean }[];
  mode: 'insert' | 'edit';
  initialHref: string;
  initialText: string;
  onApply: (href: string, displayText: string) => void;
  /** When provided, renders a Remove-link action (edit mode only). */
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [href, setHref] = useState(initialHref);
  const [displayText, setDisplayText] = useState(initialText);
  const isEdit = mode === 'edit';
  return (
    <ModalShell onClose={onClose} title={isEdit ? 'Edit link' : 'Insert link'}>
      <label className={styles.label}>URL</label>
      <input
        className={styles.input}
        type="text"
        value={href}
        onChange={(e) => setHref(e.target.value)}
        placeholder="https://... or {{ResponseUrlYes}}"
        style={{ width: '100%' }}
      />
      <div className={styles.helpText} style={{ marginTop: 4 }}>
        Quick insert:
        {' '}
        {fields.filter((f) => f.isUrl).map((f) => (
          <button
            key={f.token}
            type="button"
            onClick={() => setHref(`{{${f.token}}}`)}
            style={{
              background: 'none', border: 0, padding: 0, marginRight: 8,
              color: '#1a4f8a', cursor: 'pointer', fontSize: 11,
              textDecoration: 'underline',
            }}
          >
            {f.token}
          </button>
        ))}
      </div>
      <label className={styles.label} style={{ marginTop: 12 }}>
        Display text{' '}
        <span className={styles.helpText}>
          {isEdit
            ? '(rewrites the link text in place)'
            : '(used only when nothing is selected)'}
        </span>
      </label>
      <input
        className={styles.input}
        type="text"
        value={displayText}
        onChange={(e) => setDisplayText(e.target.value)}
        placeholder="e.g. RSVP via this link"
        style={{ width: '100%' }}
      />
      <div
        style={{
          display: 'flex', gap: 8, marginTop: 16,
          justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <div>
          {onRemove && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={onRemove}
            >
              Remove link
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => onApply(href, displayText || href)}
          >
            {isEdit ? 'Update' : 'Insert'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


function ImageDialog({
  onApply, onClose,
}: {
  onApply: (url: string, linkUrl?: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [tab, setTab] = useState<'url' | 'library'>('library');
  const assetsQ = useOutreachAssets();
  const uploadMut = useUploadOutreachAsset();

  // Shared "wrap in link?" input across all tabs. Empty string = no
  // link wrap; the caller in RichEmailEditor inserts a bare <img>.
  // When set, the caller wraps the image in <a href="..." target="_blank">.
  const trimmedLink = linkUrl.trim() || undefined;

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        const r = await uploadMut.mutateAsync(file);
        onApply(r.asset.url, trimmedLink);
      } catch (e) {
        alert(`Upload failed: ${e instanceof Error ? e.message : 'unknown'}`);
      }
    },
    [onApply, uploadMut, trimmedLink],
  );

  return (
    <ModalShell onClose={onClose} title="Insert image">
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          className={`${styles.button} ${tab === 'library' ? styles.buttonPrimary : ''}`}
          onClick={() => setTab('library')}
        >
          From library
        </button>
        <button
          type="button"
          className={`${styles.button} ${tab === 'url' ? styles.buttonPrimary : ''}`}
          onClick={() => setTab('url')}
        >
          Paste URL
        </button>
      </div>

      {tab === 'url' && (
        <>
          <label className={styles.label}>Image URL</label>
          <input
            className={styles.input}
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            style={{ width: '100%' }}
          />
        </>
      )}
      {tab === 'library' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <label className={styles.label}>Upload new image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
              }}
              disabled={uploadMut.isPending}
              style={{ fontSize: 12 }}
            />
            {uploadMut.isPending && (
              <div className={styles.helpText} style={{ marginTop: 4 }}>
                Uploading…
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid #E5E5E0', paddingTop: 12 }}>
            <div className={styles.label}>Existing assets</div>
            {assetsQ.isLoading ? (
              <div className={styles.helpText}>Loading…</div>
            ) : !assetsQ.data || assetsQ.data.length === 0 ? (
              <div className={styles.helpText}>
                No uploads yet. Upload above to add one.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 8,
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                {assetsQ.data.map((a) => (
                  <button
                    key={a.filename}
                    type="button"
                    onClick={() => onApply(a.url, trimmedLink)}
                    style={{
                      background: '#FFF', border: '1px solid #E5E5E0',
                      borderRadius: 4, padding: 4, cursor: 'pointer',
                    }}
                    title={`${a.filename} (${(a.size_bytes / 1024).toFixed(1)} KB)`}
                  >
                    <img
                      src={a.url}
                      alt={a.filename}
                      style={{
                        width: '100%', height: 80,
                        objectFit: 'cover', borderRadius: 2,
                      }}
                    />
                    <div
                      className={styles.code}
                      style={{
                        fontSize: 10, marginTop: 4, color: '#666',
                        textOverflow: 'ellipsis', overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.filename}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shared optional "Link to" field — applies to both tabs.
          When set, the inserted image is wrapped in <a> so clicking
          the image in the rendered email opens the URL. Common use:
          RSVP button image linking to a form. */}
      <div style={{ marginTop: 8, borderTop: '1px solid #E5E5E0', paddingTop: 12 }}>
        <label className={styles.label}>
          Link to URL <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          className={styles.input}
          type="text"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://example.com/rsvp"
          style={{ width: '100%' }}
        />
        <div className={styles.helpText} style={{ marginTop: 4 }}>
          When set, the image will be clickable and open this URL.
        </div>
      </div>

      {tab === 'url' ? (
        <ModalActions
          primaryLabel="Insert"
          onPrimary={() => onApply(url, trimmedLink)}
          onCancel={onClose}
        />
      ) : (
        <ModalActions
          primaryLabel="Close"
          onPrimary={onClose}
          onCancel={null}
        />
      )}
    </ModalShell>
  );
}


function ModalShell({
  title, children, onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 20, borderRadius: 8,
          minWidth: 480, maxWidth: 600,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}


function ModalActions({
  primaryLabel, onPrimary, onCancel,
}: {
  primaryLabel: string;
  onPrimary: () => void;
  onCancel: (() => void) | null;
}) {
  return (
    <div
      style={{
        display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end',
      }}
    >
      {onCancel && (
        <button
          type="button"
          className={styles.button}
          onClick={onCancel}
        >
          Cancel
        </button>
      )}
      <button
        type="button"
        className={`${styles.button} ${styles.buttonPrimary}`}
        onClick={onPrimary}
      >
        {primaryLabel}
      </button>
    </div>
  );
}
